import React, { useState, useEffect } from "react";
import api from "@api";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../../../../../context/AuthContext";
import "./LeagueTeamRosterAuth.css";
import Breadcrumbs from "../../../../../../../components/Breadcrumbs";

function LeagueTeamRosterAuth() {
  const { season_id, league_id, team_id } = useParams();
  const { isAuthenticated, loading } = useAuth();

  const [team, setTeam] = useState({ players: [], games: [] });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [playerRegistrations, setPlayerRegistrations] = useState({});
  const [activeTeamTab, setActiveTeamTab] = useState("players");
  const [isRosterEditMode, setIsRosterEditMode] = useState(false);
  const [rosterEditRows, setRosterEditRows] = useState([]);
  const [isSavingRosterEdits, setIsSavingRosterEdits] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addPlayerStep, setAddPlayerStep] = useState("names");
  const [bulkPlayerRows, setBulkPlayerRows] = useState([
    { firstName: "", lastName: "" },
    { firstName: "", lastName: "" },
    { firstName: "", lastName: "" },
    { firstName: "", lastName: "" },
    { firstName: "", lastName: "" },
  ]);
  const [playerReviewList, setPlayerReviewList] = useState([]);
  const [isCheckingPlayers, setIsCheckingPlayers] = useState(false);
  const [isSavingPlayers, setIsSavingPlayers] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: "",
    roster_id: "",
    firstName: "",
    lastName: "",
    image: null,
    jersey_number: "",
    position: "",
  });

  const domain = useSearchParams()[0].get("domain");
  const navigate = useNavigate();

  const showJerseyNumberColumn = team.players.some(
    (player) => player.jersey_number
  );

  const showPositionColumn = team.players.some(
    (player) => player.position
  );
  const sportType = team?.sport?.type?.toLowerCase() || "";

  const isRacquetSport = [
    "tennis",
    "pickleball",
    "badminton",
  ].includes(sportType);

  const positionLabel = isRacquetSport
    ? "Dominant Hand"
    : "Position";
  useEffect(() => {
    if (loading) return;

    const fetchTeam = async () => {
      try {
        let response;

        if (isAuthenticated) {
          response = await api.get(
            `/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}`,
            { withCredentials: true }
          );
        } else if (domain) {
          response = await api.get(
            `/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}?domain=${domain}`
          );
        } else {
          return setError("Unauthorized access");
        }

        setTeam(response.data.team || { players: [], games: [] });
        if (response.data.team?.organization_id) {
          await fetchPlayerRegistrations(
            response.data.team.organization_id,
            season_id
          );
        }
      } catch (err) {
        console.error("Error fetching team:", err.response?.data || err.message);
        setError("Failed to fetch team");
      }
    };

    fetchTeam();
  }, [team_id, season_id, league_id, domain, isAuthenticated, loading]);


  const fetchPlayerRegistrations = async (organizationId, seasonId) => {
    try {
      const res = await api.get("/api/player-registrations", {
        params: {
          organization_id: organizationId,
          season_id: seasonId,
        },
        withCredentials: true,
      });

      const statuses = {};

      res.data.players.forEach((player) => {
        statuses[player.id] = player.is_registered;
      });

      setPlayerRegistrations(statuses);
    } catch (err) {
      console.error("Error fetching registrations:", err);
    }
  };




  const fetchAllPlayers = async () => {
    try {
      const res = await api.get("/api/playersv2", { withCredentials: true });
      setAllPlayers(res.data.players || []);
      return res.data.players || [];
    } catch (err) {
      console.error("Error fetching all players:", err);
      setAllPlayers([]);
      return [];
    }
  };

  const normalizeName = (name) =>
    (name || "").toLowerCase().replace(/\s+/g, " ").trim();

  const getPlayerFullName = (player) =>
    `${player.firstName || ""} ${player.lastName || ""}`.trim();

  const splitPlayerName = (fullName) => {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);

    return {
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" ") || "",
    };
  };

  const getPlayerHistory = (player) => {
    const rosters = player.rosters || [];

    const history = rosters
      .map((roster) => {
        const slt = roster.seasons_leagues_team || {};
        const seasonLeague = slt.seasons_league || {};

        const teamName =
          roster.team?.name ||
          slt.team?.name ||
          "Unknown Team";

        const leagueName =
          roster.league?.name ||
          seasonLeague.league?.name ||
          "Unknown League";

        const seasonName =
          seasonLeague.season?.name ||
          roster.season?.name ||
          "Unknown Season";

        return `${teamName} — ${leagueName} — ${seasonName}`;
      })
      .filter(Boolean);

    return [...new Set(history)];
  };

  const findPotentialMatches = (typedName, playersToSearch) => {
    const normalizedTypedName = normalizeName(typedName);
    const { firstName, lastName } = splitPlayerName(typedName);

    const normalizedFirst = normalizeName(firstName);
    const normalizedLast = normalizeName(lastName);

    const exactMatches = playersToSearch.filter((player) => {
      const playerName = normalizeName(getPlayerFullName(player));
      return playerName === normalizedTypedName;
    });

    if (exactMatches.length > 0) return exactMatches.slice(0, 5);

    const possibleMatches = playersToSearch.filter((player) => {
      const playerFirst = normalizeName(player.firstName);
      const playerLast = normalizeName(player.lastName);
      const playerFull = normalizeName(getPlayerFullName(player));

      if (!normalizedTypedName) return false;

      if (normalizedFirst && normalizedLast) {
        return (
          playerFirst === normalizedFirst &&
          playerLast === normalizedLast
        );
      }

      return (
        playerFirst === normalizedTypedName ||
        playerLast === normalizedTypedName ||
        playerFull.includes(normalizedTypedName)
      );
    });

    return possibleMatches.slice(0, 5);
  };

  const resetAddPlayersModal = () => {
    setBulkPlayerRows([
      { firstName: "", lastName: "" },
      { firstName: "", lastName: "" },
      { firstName: "", lastName: "" },
      { firstName: "", lastName: "" },
      { firstName: "", lastName: "" },
    ]);
    setPlayerReviewList([]);
    setAddPlayerStep("names");
    setMessage("");
    setIsCheckingPlayers(false);
    setIsSavingPlayers(false);
  };

  const handleBulkPlayerRowChange = (index, field, value) => {
    setBulkPlayerRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const handleAddBulkPlayerRow = () => {
    setBulkPlayerRows((prev) => [
      ...prev,
      { firstName: "", lastName: "" },
    ]);
  };

  const handleRemoveBulkPlayerRow = (index) => {
    setBulkPlayerRows((prev) =>
      prev.length === 1
        ? [{ firstName: "", lastName: "" }]
        : prev.filter((_, rowIndex) => rowIndex !== index)
    );
  };




  const refreshTeam = async () => {
    const refreshedTeam = await api.get(
      `/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}`,
      { withCredentials: true }
    );

    setTeam(refreshedTeam.data.team || { players: [], games: [] });
  };

  const handleOpenAddPlayerModal = async () => {
    if (isAuthenticated && allPlayers.length === 0) {
      await fetchAllPlayers();
    }

    resetAddPlayersModal();
    setIsModalOpen(true);
  };

  const handleCheckPlayers = async (e) => {
    e.preventDefault();

    const rows = bulkPlayerRows
      .map((row) => ({
        firstName: row.firstName.trim(),
        lastName: row.lastName.trim(),
      }))
      .filter((row) => row.firstName || row.lastName);

    if (!rows.length) {
      setMessage("Please enter at least one player name.");
      return;
    }

    setIsCheckingPlayers(true);
    setMessage("");

    const playersToSearch =
      allPlayers.length > 0 ? allPlayers : await fetchAllPlayers();

    const reviewList = rows.map((row, index) => {
      const typedName = `${row.firstName} ${row.lastName}`.trim();
      const matches = findPotentialMatches(typedName, playersToSearch);

      return {
        tempId: `${typedName}-${index}`,
        typedName,
        firstName: row.firstName,
        lastName: row.lastName,
        matches,
        action: matches.length > 0 ? "existing" : "new",
        selectedPlayerId: matches[0]?.id || "",
      };
    });

    setPlayerReviewList(reviewList);
    setAddPlayerStep("review");
    setIsCheckingPlayers(false);
  };

  const updatePlayerReviewChoice = (tempId, updates) => {
    setPlayerReviewList((prev) =>
      prev.map((item) =>
        item.tempId === tempId ? { ...item, ...updates } : item
      )
    );
  };

  const handleAddPlayersToTeam = async () => {
    if (!playerReviewList.length) {
      setMessage("Please check players first.");
      return;
    }

    setIsSavingPlayers(true);
    setMessage("");

    let addedCount = 0;
    let skippedCount = 0;
    const failedPlayers = [];

    for (const item of playerReviewList) {
      try {
        if (item.action === "existing" && item.selectedPlayerId) {
          await api.post(
            `/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}/players`,
            {
              player_id: item.selectedPlayerId,
              jersey_number: "",
              position: "",
            },
            { withCredentials: true }
          );
        } else {
          const formData = new FormData();

          formData.append("firstName", item.firstName);
          formData.append("lastName", item.lastName);
          formData.append("jersey_number", "");
          formData.append("position", "");

          await api.post(
            `/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}/players`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
              withCredentials: true,
            }
          );
        }

        addedCount += 1;
      } catch (err) {
        const apiMessage = err.response?.data?.message;

        if (apiMessage === "Player already on this team") {
          skippedCount += 1;
        } else {
          failedPlayers.push(item.typedName);
          console.error(
            "Error adding player:",
            item.typedName,
            err.response?.data || err.message
          );
        }
      }
    }

    await refreshTeam();

    setIsSavingPlayers(false);
    setIsModalOpen(false);
    resetAddPlayersModal();

    if (failedPlayers.length > 0) {
      setMessage(
        `Added ${addedCount} player${addedCount === 1 ? "" : "s"}. ${skippedCount} already on team. ${failedPlayers.length} failed.`
      );
    } else if (skippedCount > 0) {
      setMessage(
        `Added ${addedCount} player${addedCount === 1 ? "" : "s"}. ${skippedCount} already on team.`
      );
    } else {
      setMessage(`Added ${addedCount} player${addedCount === 1 ? "" : "s"} to team.`);
    }

    setTimeout(() => setMessage(""), 4000);
  };

  const handleDeletePlayers = async () => {
    if (!selectedPlayers.length) {
      return alert("Please select at least one player to delete.");
    }

    if (!window.confirm("Are you sure you want to delete the selected players?")) {
      return;
    }

    try {
      await Promise.all(
        selectedPlayers.map((id) =>
          api.delete(
            `/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}/players/${id}`,
            { withCredentials: true }
          )
        )
      );

      setTeam((prev) => ({
        ...prev,
        players: prev.players.filter((p) => !selectedPlayers.includes(p.id)),
      }));

      setSelectedPlayers([]);
      setMessage("Players deleted");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error deleting players:", err);
      setMessage("Error deleting players");
      setTimeout(() => setMessage(""), 3000);
    }
  };

  const handleStartRosterEdit = () => {
    const rows = team.players.map((player) => ({
      id: player.id,
      roster_id: player.roster_id || "",
      firstName: player.firstName || "",
      lastName: player.lastName || "",
      jersey_number: player.jersey_number || "",
      position: player.position || "",
      is_registered: playerRegistrations[player.id] !== false,
    }));

    setSelectedPlayers([]);
    setRosterEditRows(rows);
    setIsRosterEditMode(true);
  };

  const handleRosterEditChange = (playerId, field, value) => {
    setRosterEditRows((prev) =>
      prev.map((row) =>
        row.id === playerId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleCancelRosterEdit = () => {
    setRosterEditRows([]);
    setIsRosterEditMode(false);
  };

  const handleSaveRosterEdits = async () => {
    try {
      setIsSavingRosterEdits(true);

      const res = await api.put(
        `/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}/players/bulk-edit`,
        {
          players: rosterEditRows.map((row) => ({
            id: row.id,
            jersey_number: row.jersey_number,
            position: row.position,
          })),
        },
        { withCredentials: true }
      );
      await Promise.all(
        rosterEditRows
          .filter(
            (row) =>
              row.is_registered !==
              (playerRegistrations[row.id] !== false)
          )
          .map((row) =>
            api.patch(
              `/api/player-registrations/${row.id}`,
              {
                is_registered: row.is_registered,
                season_id: Number(season_id), },
              { withCredentials: true }
            )
          )
      );

      setPlayerRegistrations((prev) => ({
        ...prev,
        ...Object.fromEntries(
          rosterEditRows.map((row) => [
            row.id,
            row.is_registered,
          ])
        ),
      }));
      setTeam((prev) => ({
        ...prev,
        players: prev.players.map((player) => {
          const updatedPlayer = res.data.players.find(
            (p) => Number(p.id) === Number(player.id)
          );

          if (!updatedPlayer) return player;

          return {
            ...player,
            roster_id: player.roster_id,
            jersey_number: updatedPlayer.jersey_number || null,
            position: updatedPlayer.position || null,
          };
        }),
      }));

      setIsRosterEditMode(false);
      setRosterEditRows([]);
      setMessage("Roster info updated");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error saving roster edits:", err.response?.data || err.message);
      setMessage("Error saving roster info");
      setTimeout(() => setMessage(""), 3000);
    } finally {
      setIsSavingRosterEdits(false);
    }
  };

  const openEditModal = (player) => {
    setEditForm({
      id: player.id,
      roster_id: player.roster_id || "",
      firstName: player.firstName || "",
      lastName: player.lastName || "",
      image: null,
      jersey_number: player.jersey_number || "",
      position: player.position || "",
    });

    setIsEditModalOpen(true);
  };

  const handleEditPlayer = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();

      formData.append("firstName", editForm.firstName);
      formData.append("lastName", editForm.lastName);

      if (editForm.image) {
        formData.append("image", editForm.image);
      }

      const playerRes = await api.put(
        `/api/playersv2/${editForm.id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );

      await api.put(
        `/api/seasonsv2/${season_id}/leagues/${league_id}/teams/${team_id}/players/${editForm.id}/roster`,
        {
          jersey_number: editForm.jersey_number,
          position: editForm.position,
        },
        { withCredentials: true }
      );

      setTeam((prev) => ({
        ...prev,
        players: prev.players.map((p) =>
          p.id === playerRes.data.player.id
            ? {
              ...p,
              ...playerRes.data.player,
              roster_id: p.roster_id,
              jersey_number: editForm.jersey_number || null,
              position: editForm.position || null,
            }
            : p
        ),
      }));

      setIsEditModalOpen(false);
      setMessage("Player updated");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error updating player:", err);
      setMessage("Error updating player");
      setTimeout(() => setMessage(""), 3000);
    }
  };

  const sortedGames = [...team.games].sort((a, b) => {
    const dateA = new Date(`${a.date} ${a.time}`);
    const dateB = new Date(`${b.date} ${b.time}`);
    return dateA - dateB;
  });
  console.log(team)
  return (
    <div className="teamAuthPage">
      <Breadcrumbs />

      <div className="teamAuthPage-tabs">
        <button
          type="button"
          className={
            activeTeamTab === "players"
              ? "teamAuthPage-tabBtn teamAuthPage-tabBtnActive"
              : "teamAuthPage-tabBtn"
          }
          onClick={() => setActiveTeamTab("players")}
        >
          Players
        </button>

        <button
          type="button"
          className={
            activeTeamTab === "games"
              ? "teamAuthPage-tabBtn teamAuthPage-tabBtnActive"
              : "teamAuthPage-tabBtn"
          }
          onClick={() => setActiveTeamTab("games")}
        >
          Games
        </button>
      </div>

      {activeTeamTab === "players" && (
        <div className="teamAuthPage-rosterCard">
          <div className="teamAuthPage-rosterHeader">
            <div>
              <h2 className="teamAuthPage-title">
                Players
              </h2>

              <p className="teamAuthPage-subtitle">
                {isRosterEditMode
                  ? "Update jersey numbers and positions for this team roster."
                  : "Add, edit, or remove players from this team roster."}
              </p>
            </div>

            {!isRosterEditMode ? (
              <div className="teamAuthPage-actions">
                <button
                  type="button"
                  className="teamAuthPage-actionBtn teamAuthPage-addBtn"
                  onClick={handleOpenAddPlayerModal}
                >
                  + Add Players
                </button>

                <button
                  type="button"
                  className="teamAuthPage-actionBtn teamAuthPage-editPlayersBtn"
                  onClick={handleStartRosterEdit}
                  disabled={team.players.length === 0}
                >
                  Edit Roster Info
                </button>

                <button
                  type="button"
                  className="teamAuthPage-actionBtn teamAuthPage-deleteBtn"
                  onClick={handleDeletePlayers}
                  disabled={selectedPlayers.length === 0}
                  title={
                    selectedPlayers.length === 0
                      ? "Select players first"
                      : "Delete selected players"
                  }
                >
                  🗑️ Delete Selected
                </button>
              </div>
            ) : (
              <div className="teamAuthPage-actions teamAuthPage-editActions">
                <button
                  type="button"
                  className="teamAuthPage-actionBtn teamAuthPage-saveBtn"
                  onClick={handleSaveRosterEdits}
                  disabled={isSavingRosterEdits}
                >
                  {isSavingRosterEdits ? "Saving..." : "Save Changes"}
                </button>

                <button
                  type="button"
                  className="teamAuthPage-actionBtn teamAuthPage-cancelBtn"
                  onClick={handleCancelRosterEdit}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="teamAuthPage-error">
              {error}
            </div>
          )}

          {message && !isModalOpen && (
            <div className="teamAuthPage-message">
              {message}
            </div>
          )}

          {isRosterEditMode && (
            <div className="teamAuthPage-editNotice">
              Names are locked here to protect player history. Jersey number and position only update this team roster.
            </div>
          )}

          <div className="teamAuthPage-tableWrap">
            <table className="teamAuthPage-playerTable">
              <thead>
                <tr>
                  {!isRosterEditMode && (
                    <th className="teamAuthPage-checkboxCol">
                      <input
                        type="checkbox"
                        checked={
                          team.players.length > 0 &&
                          selectedPlayers.length === team.players.length
                        }
                        onChange={() =>
                          setSelectedPlayers(
                            selectedPlayers.length === team.players.length
                              ? []
                              : team.players.map((p) => p.id)
                          )
                        }
                      />
                    </th>
                  )}

                  <th>Name</th>

                  {(showJerseyNumberColumn || isRosterEditMode) && <th>#</th>}

                  {(showPositionColumn || isRosterEditMode) && <th>{positionLabel}</th>}
                  <th>Registration</th>
                </tr>
              </thead>

              <tbody>
                {team.players.length === 0 ? (
                  <tr>
                    <td
                      colSpan={
                        1 +
                        (showJerseyNumberColumn || isRosterEditMode ? 1 : 0) +
                        (showPositionColumn || isRosterEditMode ? 1 : 0) +
                        (!isRosterEditMode ? 1 : 0)
                      }
                      className="teamAuthPage-emptyCell"
                    >
                      No players added yet.
                    </td>
                  </tr>
                ) : (
                  team.players.map((player) => {
                    const editRow = rosterEditRows.find((row) => row.id === player.id);

                    return (
                      <tr key={player.roster_id || player.id}>
                        {!isRosterEditMode && (
                          <td className="teamAuthPage-checkboxCol">
                            <input
                              type="checkbox"
                              checked={selectedPlayers.includes(player.id)}
                              onChange={() =>
                                setSelectedPlayers((prev) =>
                                  prev.includes(player.id)
                                    ? prev.filter((id) => id !== player.id)
                                    : [...prev, player.id]
                                )
                              }
                            />
                          </td>
                        )}

                        <td

                          className="teamAuthPage-playerNameCell teamAuthPage-clickablePlayerName"

                          onClick={() => navigate(`/dashboard/players/${player.id}`)}
                        >
                          <span
                            style={{
                              color:
                                playerRegistrations[player.id] === false
                                  ? "#dc2626"
                                  : "inherit",
                              fontWeight:
                                playerRegistrations[player.id] === false
                                  ? "700"
                                  : "inherit",
                            }}
                          >
                            {player.firstName} {player.lastName}
                          </span>
                        </td>

                        {(showJerseyNumberColumn || isRosterEditMode) && (
                          <td>
                            {isRosterEditMode ? (
                              <input
                                className="teamAuthPage-editInput teamAuthPage-numberInput"
                                type="text"
                                value={editRow?.jersey_number || ""}
                                onChange={(e) =>
                                  handleRosterEditChange(
                                    player.id,
                                    "jersey_number",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              player.jersey_number || "-"
                            )}
                          </td>
                          
                        )}

                        {(showPositionColumn || isRosterEditMode) && (
                          <td>
                            {isRosterEditMode ? (
                              <input
                                className="teamAuthPage-editInput teamAuthPage-positionInput"
                                type="text"
                                value={editRow?.position || ""}
                                onChange={(e) =>
                                  handleRosterEditChange(
                                    player.id,
                                    "position",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              player.position || "-"
                            )}
                          </td>
                          
                        )}

                        <td>
                          {isRosterEditMode ? (
                            <select
                              className="teamAuthPage-editInput"
                              value={String(editRow?.is_registered ?? true)}
                              onChange={(e) =>
                                handleRosterEditChange(
                                  player.id,
                                  "is_registered",
                                  e.target.value === "true"
                                )
                              }
                            >
                              <option value="true">Registered</option>
                              <option value="false">Unregistered</option>
                            </select>
                          ) : (
                            <span
                              style={{
                                color:
                                  playerRegistrations[player.id] === false
                                    ? "#dc2626"
                                    : "#16a34a",
                                fontWeight: "600",
                              }}
                            >
                              {playerRegistrations[player.id] === false
                                ? "Unregistered"
                                : "Registered"}
                            </span>
                          )}
                        </td>

                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTeamTab === "games" && (
        <div className="teamAuthPage-gamesCard">
          <div className="teamAuthPage-gamesHeader">
            <div>
              <h2 className="teamAuthPage-title">
                Games
              </h2>

              <p className="teamAuthPage-subtitle">
                View this team&apos;s scheduled games and results.
              </p>
            </div>
          </div>

          <div className="teamAuthPage-gamesSection">
            <table className="teamAuthPage-gameTable">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Matchup</th>
                  <th>Time</th>
                  <th>Results</th>
                </tr>
              </thead>

              <tbody>
                {sortedGames.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="teamAuthPage-emptyCell">
                      No games scheduled yet.
                    </td>
                  </tr>
                ) : (
                  sortedGames.map((game, index) => (
                    <tr
                      key={game.id || index}
                      onClick={() => navigate(`/dashboard/games/${game.id}`)}
                      className="teamAuthPage-gameRow"
                    >
                      <td className="teamAuthPage-gameNumberCell">{index + 1}</td>
                      <td>{game.date}</td>
                      <td>{game.team1} vs {game.team2}</td>
                      <td>{game.time}</td>
                      <td>{game.score_team1} - {game.score_team2}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {isModalOpen && (
        <div className="teamAuthPage-modalOverlay">
          <div className="teamAuthPage-addPlayersModal">
            <h2 className="teamAuthPage-modalTitle">Add Players</h2>

            <p className="teamAuthPage-modalSubtitle">
              Add new players or connect existing players to this team roster.
            </p>

            {message && (
              <div className="teamAuthPage-alert">
                {message}
              </div>
            )}

            {addPlayerStep === "names" && (
              <form onSubmit={handleCheckPlayers}>
                <div className="teamAuthPage-playerRows">
                  {bulkPlayerRows.map((row, index) => (
                    <div className="teamAuthPage-playerRow" key={index}>
                      <input
                        className="teamAuthPage-modalInput"
                        type="text"
                        placeholder="First name"
                        value={row.firstName}
                        onChange={(e) =>
                          handleBulkPlayerRowChange(index, "firstName", e.target.value)
                        }
                      />

                      <input
                        className="teamAuthPage-modalInput"
                        type="text"
                        placeholder="Last name"
                        value={row.lastName}
                        onChange={(e) =>
                          handleBulkPlayerRowChange(index, "lastName", e.target.value)
                        }
                      />

                      <button
                        type="button"
                        className="teamAuthPage-removeRowBtn"
                        onClick={() => handleRemoveBulkPlayerRow(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="teamAuthPage-addRowBtn"
                  onClick={handleAddBulkPlayerRow}
                >
                  + Add Another Row
                </button>

                <div className="teamAuthPage-modalActions">
                  <button
                    type="submit"
                    className="teamAuthPage-modalSubmitBtn"
                    disabled={isCheckingPlayers}
                  >
                    {isCheckingPlayers ? "Checking..." : "Check Players"}
                  </button>

                  <button
                    type="button"
                    className="teamAuthPage-modalCancelBtn"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetAddPlayersModal();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {addPlayerStep === "review" && (
              <>
                <div className="teamAuthPage-reviewList">
                  {playerReviewList.map((item) => (
                    <div className="teamAuthPage-reviewCard" key={item.tempId}>
                      <div className="teamAuthPage-reviewCardHeader">
                        <div>
                          <p className="teamAuthPage-reviewLabel">Player Entered</p>
                          <h3>{item.typedName}</h3>
                        </div>

                        {item.matches.length > 0 ? (
                          <span className="teamAuthPage-matchBadge">
                            Possible Match
                          </span>
                        ) : (
                          <span className="teamAuthPage-newBadge">
                            New Player
                          </span>
                        )}
                      </div>

                      {item.matches.length > 0 && (
                        <div className="teamAuthPage-matchList">
                          <p className="teamAuthPage-matchTitle">
                            Choose an existing player:
                          </p>

                          {item.matches.map((match) => (
                            <button
                              type="button"
                              key={match.id}
                              className={
                                item.action === "existing" &&
                                  Number(item.selectedPlayerId) === Number(match.id)
                                  ? "teamAuthPage-matchCard teamAuthPage-selected"
                                  : "teamAuthPage-matchCard"
                              }
                              onClick={() =>
                                updatePlayerReviewChoice(item.tempId, {
                                  action: "existing",
                                  selectedPlayerId: match.id,
                                })
                              }
                            >
                              <div className="teamAuthPage-matchMainRow">
                                <strong>
                                  {match.firstName} {match.lastName}
                                </strong>
                                <span>Use Player</span>
                              </div>

                              {getPlayerHistory(match).length > 0 && (
                                <div className="teamAuthPage-historyList">
                                  {getPlayerHistory(match).map((historyItem) => (
                                    <small key={historyItem}>{historyItem}</small>
                                  ))}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      <button
                        type="button"
                        className={
                          item.action === "new"
                            ? "teamAuthPage-createNewCard teamAuthPage-selected"
                            : "teamAuthPage-createNewCard"
                        }
                        onClick={() =>
                          updatePlayerReviewChoice(item.tempId, {
                            action: "new",
                            selectedPlayerId: "",
                          })
                        }
                      >
                        <div>
                          <strong>Create New Player</strong>
                          <small>
                            Add {item.typedName} as a new player for this roster.
                          </small>
                        </div>

                        <span>Create New</span>
                      </button>
                    </div>
                  ))}
                </div>

                <div className="teamAuthPage-modalActions">
                  <button
                    type="button"
                    className="teamAuthPage-modalSubmitBtn"
                    onClick={handleAddPlayersToTeam}
                    disabled={isSavingPlayers}
                  >
                    {isSavingPlayers ? "Saving..." : "Add Players"}
                  </button>

                  <button
                    type="button"
                    className="teamAuthPage-modalCancelBtn"
                    onClick={() => setAddPlayerStep("names")}
                  >
                    Back
                  </button>

                  <button
                    type="button"
                    className="teamAuthPage-modalCancelBtn"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetAddPlayersModal();
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div >
  );
}

export default LeagueTeamRosterAuth;